import { ButtonsListContainer } from 'components/Button';
import { RMCustomButtonProps } from 'components/customComponents/CommonComponents/RMCustomButton/RMCustomButton';
import React from 'react';
import { Modal } from 'react-bootstrap';
import { Range, getTrackBackground } from 'react-range';
import { useRMDispatch } from 'redux/hooks';
import {
  TraversalDirection,
  sidePanelButtonsActiveBorderColor,
  sidePanelButtonsActiveButtonColor,
  sidePanelButtonsActiveIconColor,
  sidePanelButtonsInActiveBorderColor,
  sidePanelButtonsInActiveButtonColor,
  sidePanelButtonsInActiveIconColor,
} from './constants';
import {
  useAllPlates,
  useLastSelectedWellNavigationDetails,
} from 'redux/hooks/experiment';
import { getWellNavigationDetails } from './utils';
import { PlateWellDTO } from 'business/models/Models';
import { useCanvasPreference } from 'redux/hooks/common';
import { ECanvasPreferencesKeys } from 'enums/ECanvasPreferencesKeys';
import { showAlertSnackBar } from 'utils/Global';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import { DropUrlParameters } from 'components/Experiment/constants';
import { setWellNavigationDetails } from 'redux/reducers/experimentDataReducer';
import { useNavigate } from 'react-router-dom';

interface Props {
  slideShowDirection: TraversalDirection;
  setSlideShowDirection: React.Dispatch<React.SetStateAction<TraversalDirection>>;
  pauseSlideShow: boolean;
  setPauseSlideShow: React.Dispatch<React.SetStateAction<boolean>>;
  slideShowSpeed: number[];
  setSlideShowSpeed: (values: number[]) => void;
  selectedSidePanelTab: any;
  setSelectedSidePanelTab: React.Dispatch<React.SetStateAction<any>>;
  startSlideShow: (traversalDirection?: TraversalDirection) => void;
  stopSlideShow: () => void;
  imagedWells: PlateWellDTO[];
}
const Footer = (props: Props) => {
  const {
    slideShowDirection,
    setSlideShowDirection,
    pauseSlideShow,
    setPauseSlideShow,
    slideShowSpeed,
    setSlideShowSpeed,
    selectedSidePanelTab,
    setSelectedSidePanelTab,
    startSlideShow,
    stopSlideShow,
    imagedWells,
  } = props;
  const dispatch = useRMDispatch();
  const allPlates = useAllPlates();
  const navigate = useNavigate();
  const restartWellNavigationOnceEnd = useCanvasPreference(
    ECanvasPreferencesKeys.RestartWellNavigationOnceEnd,
  );
  const wellNavigationDetails = useLastSelectedWellNavigationDetails();

  const changeTraversalDirection = (traversalDirection: TraversalDirection) => {
    stopSlideShow();
    setSlideShowDirection(traversalDirection);
    if (!pauseSlideShow) {
      setTimeout(() => {
        startSlideShow(traversalDirection);
      }, 500);
    }
  };

  const handleSlideShowSpeedChange = (values) => {
    stopSlideShow();
    if (!pauseSlideShow) {
      setTimeout(() => {
        startSlideShow();
      }, 500);
    }
  };

  const toggleSidePanelSelection = (key) => {
    setSelectedSidePanelTab((prev) => (prev === key ? null : key));
  };

  const slideShowControlButtons: RMCustomButtonProps[] = [
    {
      iconProps: {
        id: 'footer-controlbutton-skipbackward',
        name: 'SkipBackward',
        fill: 'white',
        width: '2vh',
        height: '2vh',
      },
      titleTip: 'Step backward',
      contentContainerClass: 'bg-[#4CAF50] w-[3.5vh] h-[3.5vh]',
      isDisabled: !pauseSlideShow,
      onClick: () => {
        const nextWellDetails = getWellNavigationDetails(
          allPlates,
          wellNavigationDetails,
          -1,
          slideShowDirection,
          imagedWells,
        );
        const parts = window.location.pathname.split('/');
        parts[DropUrlParameters.PLATEID] = nextWellDetails.plateID.toString();
        parts[DropUrlParameters.WELLNUM] = nextWellDetails.wellNum.toString();
        navigate(parts.join('/'), { replace: true });
        dispatch(setWellNavigationDetails([nextWellDetails]));
      },
    },
    {
      iconProps: {
        id: 'footer-controlbutton-play',
        name: 'Play',
        fill: 'white',
        width: '2vh',
        height: '2vh',
      },
      titleTip: 'Play',
      contentContainerClass: 'bg-[#4CAF50] w-[3.5vh] h-[3.5vh]',
      isDisabled: !pauseSlideShow,
      onClick: () => {
        setPauseSlideShow(false);
        startSlideShow();
      },
    },
    {
      iconProps: {
        id: 'footer-controlbutton-pause',
        name: 'Pause',
        fill: 'white',
        width: '2vh',
        height: '2vh',
      },
      titleTip: 'Pause',
      contentContainerClass: 'bg-[#4CAF50] w-[3.5vh] h-[3.5vh]',
      isDisabled: pauseSlideShow,
      onClick: () => {
        setPauseSlideShow(true);
        stopSlideShow();
      },
    },
    {
      iconProps: {
        id: 'footer-controlbutton-skipforward',
        name: 'SkipForward',
        fill: 'white',
        width: '2vh',
        height: '2vh',
      },
      titleTip: 'Step forward',
      contentContainerClass: 'bg-[#4CAF50] w-[3.5vh] h-[3.5vh]',
      isDisabled: !pauseSlideShow,
      onClick: () => {
        const nextWellDetails = getWellNavigationDetails(
          allPlates,
          wellNavigationDetails,
          1,
          slideShowDirection,
          imagedWells,
        );
        if (
          nextWellDetails.plateID === imagedWells[imagedWells.length - 1]?.plateId &&
          nextWellDetails.wellNum === imagedWells[imagedWells.length - 1]?.wellNumber
        ) {
          showAlertSnackBar(
            EAlertType.INFO,
            ESnackBarPosition.TOP,
            "You've reached the end of the experiment",
          );
        }
        if (
          nextWellDetails.plateID === imagedWells[0]?.plateId &&
          nextWellDetails.wellNum === imagedWells[0]?.wellNumber &&
          !restartWellNavigationOnceEnd
        ) {
          return;
        }
        const parts = window.location.pathname.split('/');
        parts[DropUrlParameters.PLATEID] = nextWellDetails.plateID.toString();
        parts[DropUrlParameters.WELLNUM] = nextWellDetails.wellNum.toString();
        navigate(parts.join('/'), { replace: true });
        dispatch(setWellNavigationDetails([nextWellDetails]));
      },
    },
  ];

  const slideShowTraversalButtons: RMCustomButtonProps[] = [
    {
      iconProps: {
        id: 'footer-controlbutton-traversebyrow',
        name: 'TraverseByRow',
        fill: '#414042',
        width: '2.5vh',
        height: '2.5vh',
      },
      titleTip: 'Traverse by row',
      contentContainerClass: `w-[4vh] h-[4vh] border-solid border-${
        slideShowDirection === TraversalDirection.Row ? '[#006AF5]' : '[#CACACA]'
      } border-[1px] border-radius rounded-l-lg rounded-r-[0] ${
        slideShowDirection === TraversalDirection.Row ? 'bg-[#AACDFC]' : ''
      }`,
      onClick: () => {
        changeTraversalDirection(TraversalDirection.Row);
      },
    },
    {
      iconProps: {
        id: 'footer-controlbutton-traversebycolumn',
        name: 'TraverseByColumn',
        fill: '#414042',
        width: '2.5vh',
        height: '2.5vh',
      },
      titleTip: 'Traverse by column',
      contentContainerClass: `w-[4vh] h-[4vh] border-solid border-${
        slideShowDirection === TraversalDirection.Column ? '[#006AF5]' : '[#CACACA]'
      } border-[1px] border-radius rounded-r-lg rounded-l-[0] ${
        slideShowDirection === TraversalDirection.Column ? 'bg-[#AACDFC]' : ''
      }`,
      onClick: () => {
        changeTraversalDirection(TraversalDirection.Column);
      },
    },
  ];

  const sidePanelButtons: RMCustomButtonProps[] = [
    {
      iconProps: {
        id: 'footer-sidepanelbutton-imagetools',
        name: 'ImageTools',
        fill:
          selectedSidePanelTab === 'tools'
            ? sidePanelButtonsActiveIconColor
            : sidePanelButtonsInActiveIconColor,
        width: '2.5vh',
        height: '2.5vh',
      },
      titleTip: 'Image tools',
      contentContainerClass: `w-[4vh] h-[4vh] border-solid border-[${
        selectedSidePanelTab === 'tools'
          ? sidePanelButtonsActiveBorderColor
          : sidePanelButtonsInActiveBorderColor
      }] border-[1px] border-radius rounded-l-lg bg-[${
        selectedSidePanelTab === 'tools'
          ? sidePanelButtonsActiveButtonColor
          : sidePanelButtonsInActiveButtonColor
      }]`,
      onClick: () => {
        toggleSidePanelSelection('tools');
      },
      isDisabled: !pauseSlideShow,
    },
    {
      iconProps: {
        id: 'footer-sidepanelbutton-info',
        name: 'Info',
        fill:
          selectedSidePanelTab === 'info'
            ? sidePanelButtonsActiveIconColor
            : sidePanelButtonsInActiveIconColor,
        width: '2.5vh',
        height: '2.5vh',
      },
      titleTip: 'Info',
      contentContainerClass: `w-[4vh] h-[4vh] border-solid border-[${
        selectedSidePanelTab === 'info'
          ? sidePanelButtonsActiveBorderColor
          : sidePanelButtonsInActiveBorderColor
      }] border-[1px] border-radius rounded-l-lg bg-[${
        selectedSidePanelTab === 'info'
          ? sidePanelButtonsActiveButtonColor
          : sidePanelButtonsInActiveButtonColor
      }]`,
      onClick: () => {
        toggleSidePanelSelection('info');
      },
      isDisabled: !pauseSlideShow,
    },
  ];
  return (
    <Modal.Footer
      id="footer-modal-container"
      className="flex flex-col w-full !px-2 !mx-2"
    >
      <div id="footer-traversal-buttons-container" className="h-full w-1/3 m-0">
        <ButtonsListContainer
          containerStyleClass="!justify-start"
          buttonsList={slideShowTraversalButtons}
        />
      </div>
      <div id="footer-control-buttons-container" className="h-full w-1/3 m-0">
        <ButtonsListContainer
          containerStyleClass="!justify-center"
          buttonsList={slideShowControlButtons}
          marginBetweenItems={7}
        />
      </div>
      <div id="footer-speed-and-sidepanel-container" className="h-full w-1/3 m-0">
        <div id="footer-speedpanel-wrapper" className="w-full h-full flex flex-row">
          <div id="footer-speedpanel-left" className="flex flex-row w-4/5 !justify-end">
            <div id="footer-speedpanel-label-slow" className="w-[15%] h-full pt-[5.5px]">
              Slow
            </div>
            <div id="footer-speedpanel-slider-container" className="w-[70%] h-full pt-3">
              <Range
                min={0}
                max={4}
                values={slideShowSpeed}
                onChange={(values) => {
                  setSlideShowSpeed(values);
                }}
                onFinalChange={handleSlideShowSpeedChange}
                renderMark={({ props, index }) => (
                  <div
                    {...props}
                    id={`footer-slideshow-speed-mark-${index}`}
                    style={{
                      ...props.style,
                      backgroundColor: '#313131',
                      height: '1.2vh',
                      marginTop: '2.5vh',
                      width: 1,
                    }}
                  />
                )}
                renderTrack={({ props, children }) => (
                  <div
                    {...props}
                    id="footer-slideshow-speed-track"
                    style={{
                      ...props.style,
                      background: getTrackBackground({
                        values: slideShowSpeed,
                        colors: ['#F7A246', '#E0E0E0'],
                        min: 0,
                        max: 4,
                      }),
                      height: '10%',
                      borderRadius: '3rem',
                    }}
                  >
                    {children}
                  </div>
                )}
                renderThumb={({ props }) => (
                  <div
                    {...props}
                    id="footer-slideshow-speed-thumb"
                    className="h-[2vh] w-[2vh] bg-[#F7A246] rounded-full outline-none shadow-md focus:outline-none focus:shadow-md"
                    style={{
                      ...props.style,
                    }}
                  />
                )}
              />
            </div>
            <div
              id="footer-speedpanel-label-fast"
              className="flex w-[15%] h-full pt-[5.5px] justify-end"
            >
              Fast
            </div>
          </div>
          <div
            id="footer-sidepanel-buttons-container"
            className="flex flex-row w-1/5 h-full !justify-end !px-2 !mx-2"
          >
            <ButtonsListContainer
              containerStyleClass="!justify-center"
              buttonsList={sidePanelButtons}
              marginBetweenItems={7}
            />
          </div>
        </div>
      </div>
    </Modal.Footer>
  );
};

export default Footer;
